import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Calendar } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useSEO from "../hooks/useSEO";
import { articles, getArticleBySlug, formatDate } from "../data/articles";

const renderBlock = (block, i) => {
  if (block.type === "h2") return <h2 key={i}>{block.text}</h2>;
  if (block.type === "h3") return <h3 key={i}>{block.text}</h3>;
  if (block.type === "p") return <p key={i}>{block.text}</p>;
  return null;
};

const BlogArticle = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  useSEO(
    article
      ? {
          title: article.meta.title,
          description: article.meta.description,
          keywords: article.meta.keywords,
          image: article.cover,
          type: "article",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.meta.description,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            author: {
              "@type": "Person",
              name: "Catherine SAC-EPEE",
              jobTitle: "Praticienne en Shiatsu",
            },
            publisher: {
              "@type": "Organization",
              name: "Catherine' Shiatsu",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": window.location.href,
            },
          },
        }
      : {}
  );

  if (!article) return <Navigate to="/blog" replace />;

  const related = articles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 2);

  return (
    <div className="site">
      <Header />
      <main className="page-main">
        <article className="article">
          <div className="article-hero" style={{ backgroundImage: `url(${article.cover})` }}>
            <div className="article-hero-overlay" />
            <div className="article-hero-inner">
              <Link to="/blog" className="back-link on-dark"><ArrowLeft size={16} /> Retour au blog</Link>
              <span className="article-category">{article.category}</span>
              <h1 className="article-title">{article.title}</h1>
              <div className="article-meta">
                <span><Calendar size={14} /> {formatDate(article.date)}</span>
                <span><Clock size={14} /> {article.readTime} min de lecture</span>
              </div>
            </div>
          </div>

          <div className="article-body">
            <p className="article-lead">{article.excerpt}</p>
            {article.content.map(renderBlock)}

            <div className="article-cta">
              <h3>Envie de découvrir les bienfaits du Shiatsu ?</h3>
              <p>
                Prenez rendez-vous pour une séance personnalisée au cabinet de La Ciotat.
              </p>
              <Link to="/#contact" className="btn btn-primary">PRENDRE UN RDV</Link>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="section related-articles">
            <h2 className="related-title">À lire également</h2>
            <div className="blog-grid">
              {related.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`} className="blog-card">
                  <div className="blog-cover">
                    <img src={a.cover} alt={a.title} loading="lazy" />
                  </div>
                  <div className="blog-body">
                    <div className="blog-meta">
                      <span className="blog-category">{a.category}</span>
                    </div>
                    <h3 className="blog-title">{a.title}</h3>
                    <p className="blog-excerpt">{a.excerpt}</p>
                    <span className="blog-more">
                      Lire l’article <ArrowRight size={16} strokeWidth={1.6} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogArticle;
